/**
 * Per-team control map utilities (PMA-COCK-162).
 *
 * Orientation convention (decided 2026-09-04, matches the rest of the
 * logger's location-based analytics — heatMapZones.ts, AreaControlGrid):
 * event locations live in ONE absolute StatsBomb frame, x ∈ [0, 120],
 * y ∈ [0, 80], for the whole match. The logger draws home on the left, so
 * HOME attacks toward x=120 and AWAY attacks toward x=0 for the entire
 * match — no side-switch (e.g. at half-time) is recorded. Every "third",
 * "lane" and attacking-direction arrow in this file derives from that one
 * fixed convention. Capturing a real per-period attacking direction is a
 * deferred follow-up, not handled here.
 *
 * Thirds (share of a team's own located events by pitch third, oriented to
 * that team's attacking direction):
 *   - HOME (attacks +x): safe = x<40, middle = 40<=x<80, danger = x>=80
 *   - AWAY (attacks -x): mirrored — safe = x>=80, middle = 40<=x<80,
 *     danger = x<40
 *
 * Lanes (share by pitch third of the y-axis, oriented the same way — a team
 * facing +x has "left" at the top touchline, y≈0; a team facing -x has
 * "left" at the bottom touchline, y≈80):
 *   - HOME: left = y<26.67, center = 26.67<=y<53.33, right = y>=53.33
 *   - AWAY: mirrored — left = y>=53.33, center = 26.67<=y<53.33,
 *     right = y<26.67
 *
 * Percentages are always integers that sum to exactly 100 (largest-
 * remainder rounding); a team with zero located events gets total = 0 and
 * every share at 0.
 */
import type { MatchEvent } from "../../../store/useMatchLogStore";
import { sameTeamId } from "./teamId";
import {
  locationToZoneId,
  PITCH_WIDTH,
  PITCH_HEIGHT,
  TOTAL_ZONES,
  ZONES,
} from "./heatMapZones";

export type TeamSide = "home" | "away";

export interface TeamControlCell {
  /** 0-based zone id (row-major), matches ZONES / heatMapZones. */
  zoneId: number;
  /** Raw event count this team generated in this zone. */
  count: number;
  /** count / max cell count for this team, 0..1 (0 when the team has no located events). */
  intensity: number;
}

export interface TeamControlThirds {
  safe: number;
  middle: number;
  danger: number;
}

export interface TeamControlLanes {
  left: number;
  center: number;
  right: number;
}

export interface TeamControlMapData {
  cells: TeamControlCell[];
  thirds: TeamControlThirds;
  lanes: TeamControlLanes;
  /** Total located events this team generated. */
  total: number;
}

const THIRD_X_LOW = PITCH_WIDTH / 3; // 40
const THIRD_X_HIGH = (2 * PITCH_WIDTH) / 3; // 80
const LANE_Y_LOW = PITCH_HEIGHT / 3; // 26.667
const LANE_Y_HIGH = (2 * PITCH_HEIGHT) / 3; // 53.333

type ThirdKey = keyof TeamControlThirds;
type LaneKey = keyof TeamControlLanes;

function thirdBucket(x: number, side: TeamSide): ThirdKey {
  const isLowX = x < THIRD_X_LOW;
  const isHighX = x >= THIRD_X_HIGH;
  if (side === "home") {
    if (isLowX) return "safe";
    if (isHighX) return "danger";
    return "middle";
  }
  // Away attacks -x: mirrored.
  if (isLowX) return "danger";
  if (isHighX) return "safe";
  return "middle";
}

function laneBucket(y: number, side: TeamSide): LaneKey {
  const isTopThird = y < LANE_Y_LOW;
  const isBottomThird = y >= LANE_Y_HIGH;
  if (side === "home") {
    if (isTopThird) return "left";
    if (isBottomThird) return "right";
    return "center";
  }
  // Away attacks -x: mirrored.
  if (isTopThird) return "right";
  if (isBottomThird) return "left";
  return "center";
}

/**
 * Convert raw counts into integer percentages that sum to exactly 100,
 * using largest-remainder rounding. Ties in the fractional remainder keep
 * the original bucket order (stable sort).
 */
function largestRemainderPercentages(
  counts: number[],
  total: number,
): number[] {
  if (total <= 0) return counts.map(() => 0);

  const raw = counts.map((c) => (c / total) * 100);
  const floors = raw.map(Math.floor);
  const flooredSum = floors.reduce((a, b) => a + b, 0);
  let remainder = Math.round(100 - flooredSum);

  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);

  const result = [...floors];
  for (let k = 0; k < order.length && remainder > 0; k++) {
    result[order[k].i] += 1;
    remainder--;
  }
  return result;
}

/**
 * Shared math for both `computeTeamControlMap` and `computePlayerControlMap`:
 * per-cell intensity on the shared 6x4 zone grid, plus thirds/lanes shares
 * oriented to `side`'s attacking direction (see file header for the
 * convention). `isSubject` decides which located events count — a team
 * membership check for the team variant, a player-authorship check for the
 * player variant. Any event type is eligible (mirrors computeAreaControlData).
 */
function computeControlMapForSubject(
  events: MatchEvent[],
  side: TeamSide,
  isSubject: (ev: MatchEvent) => boolean,
): TeamControlMapData {
  const counts = new Array<number>(TOTAL_ZONES).fill(0);
  const thirdCounts: TeamControlThirds = { safe: 0, middle: 0, danger: 0 };
  const laneCounts: TeamControlLanes = { left: 0, center: 0, right: 0 };
  let total = 0;

  for (const ev of events) {
    if (!isSubject(ev)) continue;
    if (!ev.location || ev.location.length < 2) continue;

    const [x, y] = ev.location;
    const zoneId = locationToZoneId(x, y);
    counts[zoneId]++;
    thirdCounts[thirdBucket(x, side)]++;
    laneCounts[laneBucket(y, side)]++;
    total++;
  }

  const max = Math.max(0, ...counts);
  const cells: TeamControlCell[] = ZONES.map((zone) => ({
    zoneId: zone.id,
    count: counts[zone.id],
    intensity: max > 0 ? counts[zone.id] / max : 0,
  }));

  const [safe, middle, danger] = largestRemainderPercentages(
    [thirdCounts.safe, thirdCounts.middle, thirdCounts.danger],
    total,
  );
  const [left, center, right] = largestRemainderPercentages(
    [laneCounts.left, laneCounts.center, laneCounts.right],
    total,
  );

  return {
    cells,
    thirds: { safe, middle, danger },
    lanes: { left, center, right },
    total,
  };
}

/**
 * Compute one team's control map: per-cell intensity on the shared 6x4
 * zone grid, plus thirds/lanes shares oriented to that team's attacking
 * direction (see file header for the convention).
 *
 * Input filter mirrors computeAreaControlData: located events belonging to
 * `teamId`, any event type.
 */
export function computeTeamControlMap(
  events: MatchEvent[],
  teamId: string,
  side: TeamSide,
): TeamControlMapData {
  return computeControlMapForSubject(events, side, (ev) =>
    sameTeamId(ev.team_id, teamId),
  );
}

/**
 * Compute one PLAYER's control map (PMA-COCK-168) — identical thirds/lanes
 * math and orientation convention to `computeTeamControlMap`, filtered to
 * this player's own located events (`ev.player_id === playerId`) instead of
 * a whole team's. `side` is still the player's own team side, since the
 * attacking-direction convention is per-team, not per-player.
 */
export function computePlayerControlMap(
  events: MatchEvent[],
  playerId: string,
  side: TeamSide,
): TeamControlMapData {
  return computeControlMapForSubject(
    events,
    side,
    (ev) => ev.player_id === playerId,
  );
}
